import React from "react";

import { DateTime } from "luxon";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { Card } from "@app/analytics/Home/components/styles";
import useEventGroups from "@app/analytics/hooks/calendar/useEventGroups";
import { UserEventGroup } from "@app/analytics/types/eventGroupTypes";
import useAuth from "@app/common/auth/useAuth";
import ROUTES from "@app/common/routes";
import { getGreetingTime, pluralizePhrase } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import { Link } from "@rui/Link";
import { Column, Row, StackProps } from "@rui/Stack";
import { Bold } from "@rui/styles";

const Wave = styled<React.FC<StackProps>>((props) => (
  <Row {...props}>
    <Row className="wave__inner">👋</Row>
  </Row>
))(({ theme }) => ({
  height: "64px",
  backgroundColor: "#4462EF26",
  width: "64px",
  borderRadius: "50%",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  padding: theme.spacing(1),
  "& .wave__inner": {
    backgroundColor: theme.palette.transparent.white[20],
    borderRadius: "50%",
    height: "100%",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
  },
}));

const getUpcomingMeetingsCount = (events: UserEventGroup[]) => {
  const startOfToday = DateTime.now().startOf("day");
  return events.filter((event) => {
    return event.startTime.startOf("day").equals(startOfToday);
  }).length;
};

const withFirstName = (greeting: string, firstName?: string) => {
  return firstName ? `${greeting}, ${firstName}` : greeting;
};

const GreetingBanner = ({
  isNewUser,
  numNewUpdates,
}: {
  isNewUser: boolean;
  numNewUpdates: number;
}) => {
  const { user } = useAuth();
  const { events, isLoading } = useEventGroups();
  const { inWorkspace } = useWorkspace();

  const numUpcomingMeetings = getUpcomingMeetingsCount(events);

  const greeting = isNewUser
    ? "Welcome to your homepage"
    : `Good ${getGreetingTime()}`;

  return (
    <AutoAnimateContainer show={!isLoading} sx={{ width: "100%" }}>
      <Card
        sx={{
          padding: 2,
          marginBottom: 2,
          boxShadow: "0px 1px 16px 0px #231F380A",
          borderRadius: "8px",
          minWidth: "100%",
        }}
      >
        <Row spacing={3}>
          <Wave />
          <Column spacing={0.5}>
            <Typography variant="h2">
              {withFirstName(greeting, user?.firstName)}
            </Typography>
            {isNewUser ? (
              <Typography variant="bodySecondaryReg" color="shades.neutral.600">
                Your updates{" "}
                {inWorkspace &&
                  "and items shared across your teams and Workspace "}
                will appear here.
              </Typography>
            ) : (
              <Typography variant="bodySecondaryReg" color="shades.neutral.600">
                You have{" "}
                <Bold>
                  {numNewUpdates === 0
                    ? "no new updates"
                    : pluralizePhrase(numNewUpdates, "new update")}
                </Bold>{" "}
                below, and{" "}
                {numUpcomingMeetings === 0 ? (
                  <Bold>no upcoming meetings</Bold>
                ) : (
                  <Link to={ROUTES.CALENDAR_UPCOMING}>
                    {pluralizePhrase(numUpcomingMeetings, "upcoming meeting")}
                  </Link>
                )}
                . Have a productive day!
              </Typography>
            )}
          </Column>
        </Row>
      </Card>
    </AutoAnimateContainer>
  );
};

export default GreetingBanner;
